import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { LayoutDashboard, Kanban, Swords, Building2, Settings, LogOut, Search, Tv, Volume2, VolumeX, Menu, X, CalendarClock, Brain, BarChart3, Zap, Wallet, Receipt, FileText, BookUser, Target, GraduationCap, Bell, Database, Megaphone, ChevronDown, Sparkles, Upload, FileSpreadsheet } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { initials, resolveAvatar } from "@/lib/api";
import api from "@/lib/api";
import HeaderCountdown from "@/components/HeaderCountdown";
import MetaDiariaCard from "@/components/MetaDiariaCard";
import MinhaMetaMesCard from "@/components/MinhaMetaMesCard";
import ResumoDoDiaModal from "@/components/ResumoDoDiaModal";
import NotificationBell from "@/components/NotificationBell";
import { toast } from "sonner";

const CEAP_LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_dashboard-ceap/artifacts/b4qjckp1_nova_logo_CEAP_2020-removebg-preview.png";

function GestaoDropdown({ items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const recalc = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
  };

  useEffect(() => {
    if (!open) return;
    recalc();
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScroll = () => recalc();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const activeItem = items.find((it) => typeof window !== "undefined" && window.location.pathname.startsWith(it.to));
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${activeItem ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}
        data-testid="nav-gestao-btn"
      >
        <Settings className="w-4 h-4" /> Gestão
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="fixed min-w-[180px] bg-white rounded-xl shadow-xl border border-slate-200 py-1 z-[200]"
          style={{ top: pos.top, left: pos.left }}
          data-testid="nav-gestao-menu"
        >
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/fiscal"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${it.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2 text-sm font-medium transition ${
                  isActive ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              <it.icon className="w-4 h-4" /> {it.label}
            </NavLink>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout, updateMe } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ municipios: [], pessoas: [] });
  const [showResults, setShowResults] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [agendaBadge, setAgendaBadge] = useState(0);
  const [leadsBadge, setLeadsBadge] = useState(0);
  const [sprintOpen, setSprintOpen] = useState(false);
  const [sprintDur, setSprintDur] = useState(60);
  const [sprintBusy, setSprintBusy] = useState(false);
  const [sprintScope, setSprintScope] = useState("all"); // "all" | "select"
  const [sprintTargets, setSprintTargets] = useState([]); // vendedora ids
  const [vendedorasList, setVendedorasList] = useState([]);

  useEffect(() => {
    if (!user) return;
    const load = () => {
      Promise.all([
        api.get("/agenda/retornos").catch(() => ({ data: { total_pendentes_hoje_e_atrasados: 0 } })),
        api.get("/agenda/aniversarios").catch(() => ({ data: { hoje: [] } })),
      ]).then(([r, a]) => {
        const total = (r.data?.total_pendentes_hoje_e_atrasados || 0) + (a.data?.hoje?.length || 0);
        setAgendaBadge(total);
      });
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  // Iter 46 · Badge de leads pendentes (só para Diretoria/Supervisão)
  useEffect(() => {
    if (!user || (user.role !== "diretoria" && user.role !== "supervisao")) return;
    const loadLeads = () => {
      api.get("/leads-mkt/pendentes")
        .then((r) => setLeadsBadge((r.data || []).length))
        .catch(() => {});
    };
    loadLeads();
    const t = setInterval(loadLeads, 30000);
    // Escuta o WS: badge atualiza imediatamente quando um lead é criado/direcionado
    const bumpUp = () => setLeadsBadge((v) => v + 1);
    const bumpDown = () => setLeadsBadge((v) => Math.max(0, v - 1));
    window.addEventListener("ws:lead_pendente_direcionamento", bumpUp);
    window.addEventListener("ws:lead_direcionado", bumpDown);
    return () => {
      clearInterval(t);
      window.removeEventListener("ws:lead_pendente_direcionamento", bumpUp);
      window.removeEventListener("ws:lead_direcionado", bumpDown);
    };
  }, [user]);

  useEffect(() => {
    if (q.length < 2) { setResults({ municipios: [], pessoas: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data);
        setShowResults(true);
      } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Carrega lista de vendedoras quando o modal do Sprint abre (para Diretoria)
  useEffect(() => {
    if (sprintOpen && user?.role === "diretoria" && vendedorasList.length === 0) {
      api.get("/users").then((r) => {
        const vs = (r.data || []).filter((u) => u.role === "vendedora");
        setVendedorasList(vs);
      }).catch(() => {});
    }
  }, [sprintOpen, user?.role, vendedorasList.length]);

  const canSeeAdmin = user?.role === "diretoria" || user?.role === "supervisao";
  const canSeeDashGeral = user?.role === "diretoria";
  const canSeeDashNucleo = user?.role === "supervisao" || user?.role === "diretoria";
  const isFiscal = user?.role === "fiscal";
  const isVendedora = user?.role === "vendedora";
  const isSupervisao = user?.role === "supervisao";
  const isAnalistaMkt = user?.role === "analista_mkt";
  const isIndicador = user?.role === "indicador";
  const isGestaoCel = user?.role === "gestao_cel";
  const isVendedoraCel = user?.role === "vendedora" && user?.nucleo_nome === "CeL Consultoria";
  const isCelStaff = isGestaoCel || isVendedoraCel || user?.role === "diretoria";

  const gestaoItems = [
    { to: "/gestao-cursos", label: "Gestão de Cursos", icon: GraduationCap, show: user?.role === "diretoria" || user?.role === "supervisao" },
    { to: "/inteligencia", label: "Inteligência", icon: Brain, show: !isFiscal && !isAnalistaMkt && !isIndicador && !isGestaoCel && !isVendedoraCel },
    { to: "/pdi", label: "Gestão PDI", icon: BookUser, show: user?.role === "diretoria" },
    { to: "/fiscal", label: "Fiscal", icon: Receipt, show: user?.role === "diretoria" },
    { to: "/fiscal/faturamento", label: "Relatório de faturamento", icon: FileSpreadsheet, show: user?.role === "diretoria" },
    { to: "/minhas-indicacoes", label: "Minhas Indicações CeL", icon: Sparkles, show: !isFiscal && !isAnalistaMkt && !isIndicador && !isGestaoCel && !isVendedoraCel },
    { to: "/cel/admin", label: "Admin CeL", icon: Settings, show: user?.role === "diretoria" || isGestaoCel },
    { to: "/admin", label: "Admin", icon: Settings, show: canSeeAdmin },
  ].filter((x) => x.show);

  const nav_items = isFiscal
    ? [
        { to: "/fiscal", label: "Fiscal", icon: Receipt, show: true },
        { to: "/fiscal/faturamento", label: "Relatório de faturamento", icon: FileSpreadsheet, show: true },
        { to: "/geral", label: "Dashboard", icon: LayoutDashboard, show: true },
      ]
    : isAnalistaMkt
    ? [
        { to: "/novo-lead", label: "Analista de Marketing", icon: Target, show: true },
        { to: "/captacao", label: "Captação de Base", icon: Database, show: true },
      ]
    : isIndicador
    ? [
        { to: "/minhas-indicacoes", label: "Minhas Indicações", icon: Sparkles, show: true },
        { to: "/cel/kanban", label: "Funil CeL", icon: Kanban, show: true },
      ]
    : (isGestaoCel || isVendedoraCel)
    ? [
        { to: "/cel/kanban", label: "Funil CeL", icon: Kanban, show: true },
        { to: "/cel/carteira", label: "Carteira CeL", icon: Wallet, show: true },
        { to: "/cel/dashboard", label: "Dashboard CeL", icon: LayoutDashboard, show: true },
        { to: "/cel/leads-acao", label: "Leads de Ação", icon: Megaphone, show: isGestaoCel || user?.role === "diretoria" },
        { to: "/agenda", label: "Agenda", icon: CalendarClock, show: true, badge: agendaBadge },
        { gestao: true, label: "Gestão", icon: Settings, show: gestaoItems.length > 0, items: gestaoItems },
      ]
    : [
        { to: "/carteira", label: "Carteira", icon: Wallet, show: !isFiscal },
        { to: "/funil", label: "Funil", icon: Kanban, show: true },
        { to: "/agenda", label: "Agenda", icon: CalendarClock, show: true, badge: agendaBadge },
        { to: "/geral", label: "Dashboard", icon: LayoutDashboard, show: true },
        { to: "/minhas-nfs", label: "Minhas NFs", icon: FileText, show: isVendedora || isSupervisao },
        { to: "/leads-pendentes", label: "Leads Pendentes", icon: Target, show: user?.role === "diretoria" || user?.role === "supervisao", badge: leadsBadge },
        { gestao: true, label: "Gestão", icon: Settings, show: gestaoItems.length > 0, items: gestaoItems },
      ];
  const filtered_nav = nav_items.filter((n) => n.show);

  const toggleSom = () => updateMe({ som_ativo: !user.som_ativo });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* TOP BAR */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm no-print">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center gap-4">
          <button
            className="md:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => nav(user?.role === "fiscal" ? "/fiscal" : user?.role === "analista_mkt" ? "/novo-lead" : user?.role === "indicador" ? "/minhas-indicacoes" : (user?.role === "gestao_cel" || (user?.role === "vendedora" && user?.nucleo_nome === "CeL Consultoria")) ? "/cel/kanban" : "/funil")}
            data-testid="brand-logo"
          >
            <img
              src={CEAP_LOGO}
              alt="CEAP"
              className="w-10 h-10 object-contain"
              style={{ filter: "brightness(0)" }}
            />
            <div className="font-display font-extrabold text-lg text-slate-900 hidden sm:block tracking-tight">CRM</div>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-xl relative">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="global-search"
                value={q}
                onFocus={() => setShowResults(true)}
                onBlur={() => setTimeout(() => setShowResults(false), 200)}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar município ou pessoa..."
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-100 border border-transparent focus:bg-white focus:border-blue-500 focus:outline-none text-sm"
              />
            </div>
            {showResults && (results.municipios.length > 0 || results.pessoas.length > 0) && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-auto z-50">
                {results.municipios.length > 0 && (
                  <div className="p-2">
                    <div className="text-xs text-slate-400 uppercase font-bold px-2 py-1">Municípios</div>
                    {results.municipios.map((m) => (
                      <button
                        key={m.id}
                        onMouseDown={() => nav(`/municipio/${m.id}`)}
                        className="w-full text-left px-2 py-2 rounded hover:bg-slate-100 text-sm flex items-center gap-2"
                        data-testid={`search-mun-${m.id}`}
                      >
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {m.nome} <span className="text-slate-400">/ {m.uf}</span>
                      </button>
                    ))}
                  </div>
                )}
                {results.pessoas.length > 0 && (
                  <div className="p-2 border-t border-slate-100">
                    <div className="text-xs text-slate-400 uppercase font-bold px-2 py-1">Pessoas</div>
                    {results.pessoas.map((p) => (
                      <button
                        key={p.id}
                        onMouseDown={() => nav(`/pessoa/${p.id}`)}
                        className="w-full text-left px-2 py-2 rounded hover:bg-slate-100 text-sm"
                        data-testid={`search-pes-${p.id}`}
                      >
                        {p.nome} <span className="text-slate-400">/ {p.funcao}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            <HeaderCountdown />
            <NotificationBell />
            {user?.role === "diretoria" && (
              <button
                onClick={() => setSprintOpen(true)}
                title="Iniciar Sprint de Ligações"
                className="p-2 rounded-lg text-white bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 shadow"
                data-testid="btn-iniciar-sprint"
              >
                <Zap className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={toggleSom}
              title={user?.som_ativo ? "Silenciar" : "Ativar som"}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
              data-testid="toggle-som"
            >
              {user?.som_ativo ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            {user?.role === "diretoria" && (
              <button
                onClick={() => window.open("/tv", "_blank")}
                title="Modo TV"
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 hidden sm:block"
                data-testid="open-tv"
              >
                <Tv className="w-5 h-5" />
              </button>
            )}
            <div className="hidden md:flex items-center gap-2 pl-2 border-l border-slate-200">
              <Avatar className="w-8 h-8">
                <AvatarImage src={resolveAvatar(user?.avatar_url)} alt={user?.nome} />
                <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">
                  {initials(user?.nome)}
                </AvatarFallback>
              </Avatar>
              <div className="text-xs">
                <div className="font-semibold text-slate-900 leading-tight">{user?.nome}</div>
                <div className="text-slate-500 leading-tight">
                  {user?.role} {user?.nucleo_nome ? `· ${user.nucleo_nome}` : ""}
                </div>
              </div>
            </div>
            <button
              onClick={logout}
              className="p-2 rounded-lg hover:bg-red-50 text-slate-600 hover:text-red-600"
              title="Sair"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* NAV */}
        <nav className="border-t border-slate-100 bg-white">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 flex md:flex-row gap-1 md:gap-6 py-2 overflow-x-auto no-scrollbar">
            {filtered_nav.map((n) => (
              n.gestao ? (
                <GestaoDropdown key="gestao" items={n.items} />
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/funil" || n.to === "/fiscal"}
                  data-testid={`nav-${n.to.slice(1)}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      isActive
                        ? "bg-blue-50 text-blue-700"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" /> {n.label}
                  {n.badge > 0 && (
                    <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`nav-badge-${n.to.slice(1)}`}>
                      {n.badge > 99 ? "99+" : n.badge}
                    </span>
                  )}
                </NavLink>
              )
            ))}
          </div>
        </nav>
      </header>

      {isVendedora && user?.nucleo_nome !== "CeL Consultoria" && (
        <div className="sticky top-16 z-30 bg-slate-50/95 backdrop-blur border-b border-slate-200 no-print" data-testid="vendedora-meta-bar">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <MetaDiariaCard />
              <MinhaMetaMesCard />
            </div>
          </div>
        </div>
      )}

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 md:py-10">{children}</main>

      {/* Popup Resumo do Dia — aparece 1x por dia, templates estáticos (zero custo) */}
      <ResumoDoDiaModal user={user} />

      {/* Dialog Iniciar Sprint de Ligações */}
      <Dialog open={sprintOpen} onOpenChange={setSprintOpen}>
        <DialogContent data-testid="modal-iniciar-sprint">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-500" /> Iniciar Sprint de Ligações
            </DialogTitle>
            <DialogDescription>
              O timer e o ranking em tempo real serão exibidos para as vendedoras participantes.
              A vencedora será a que registrar mais ligações no período.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-widest">Duração (minutos)</label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {[15, 30, 60, 90].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setSprintDur(v)}
                    className={`py-2 rounded-lg font-bold text-sm transition ${sprintDur === v ? "bg-orange-500 text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-testid={`sprint-dur-${v}`}
                  >
                    {v} min
                  </button>
                ))}
              </div>
            </div>

            {user?.role === "diretoria" && (
              <div>
                <label className="text-xs font-bold uppercase text-slate-500 tracking-widest">Participantes</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setSprintScope("all")}
                    className={`py-2 rounded-lg font-bold text-sm transition ${sprintScope === "all" ? "bg-orange-500 text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-testid="sprint-scope-all"
                  >
                    Todas as vendedoras
                  </button>
                  <button
                    type="button"
                    onClick={() => setSprintScope("select")}
                    className={`py-2 rounded-lg font-bold text-sm transition ${sprintScope === "select" ? "bg-orange-500 text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-testid="sprint-scope-select"
                  >
                    Selecionar específicas
                  </button>
                </div>

                {sprintScope === "select" && (
                  <div className="mt-3 border rounded-lg divide-y max-h-56 overflow-auto bg-white" data-testid="sprint-vendedoras-list">
                    {vendedorasList.length === 0 && (
                      <div className="p-3 text-xs text-slate-500 text-center">Carregando vendedoras...</div>
                    )}
                    {vendedorasList.map((v) => {
                      const checked = sprintTargets.includes(v.id);
                      return (
                        <label
                          key={v.id}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50"
                          data-testid={`sprint-target-${v.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setSprintTargets((cur) =>
                                cur.includes(v.id) ? cur.filter((x) => x !== v.id) : [...cur, v.id]
                              )
                            }
                            className="w-4 h-4 accent-orange-500"
                          />
                          <Avatar className="w-7 h-7">
                            <AvatarImage src={resolveAvatar(v.avatar_url)} alt={v.nome} />
                            <AvatarFallback className="text-[10px]">{initials(v.nome)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-slate-800 truncate">{v.nome}</div>
                            <div className="text-[11px] text-slate-500 truncate">{v.nucleo_nome || "—"}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {sprintScope === "select" && (
                  <div className="mt-2 text-[11px] text-slate-500">
                    {sprintTargets.length} selecionada{sprintTargets.length === 1 ? "" : "s"}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSprintOpen(false)} disabled={sprintBusy}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (user?.role === "diretoria" && sprintScope === "select" && sprintTargets.length === 0) {
                  toast.error("Selecione ao menos uma vendedora ou escolha 'Todas'.");
                  return;
                }
                setSprintBusy(true);
                try {
                  const payload = { duracao_min: sprintDur };
                  if (user?.role === "diretoria" && sprintScope === "select") {
                    payload.target_vendedoras = sprintTargets;
                  }
                  await api.post("/sprints/iniciar", payload);
                  const escopoLabel = (user?.role === "diretoria" && sprintScope === "select")
                    ? ` para ${sprintTargets.length} vendedora${sprintTargets.length === 1 ? "" : "s"}`
                    : "";
                  toast.success(`Sprint de ${sprintDur} min iniciado${escopoLabel}! 🔥`);
                  setSprintOpen(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erro ao iniciar sprint");
                } finally {
                  setSprintBusy(false);
                }
              }}
              disabled={sprintBusy}
              className="bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white"
              data-testid="btn-confirmar-sprint"
            >
              <Zap className="w-4 h-4 mr-1" /> {sprintBusy ? "Iniciando..." : "Iniciar sprint"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
